import { useMemo, useState } from 'react';
import { MessageSquarePlus, MessagesSquare, Search, Users } from 'lucide-react';
import { Alert, Button, EmptyState, IconButton, Input, SkeletonText, Tooltip } from '../../ui';
import { chatErr, filterConversations, isChatStaffOnlyError, useChatConversations } from './chatApi';
import { ChatThread } from './ChatThread';
import { ConversationRow, NewChatDialog, NewGroupDialog } from './ChatParts';

/**
 * Team chat inside the floating chat launcher — the same data and behaviour as the `/chat` page,
 * squeezed into the panel: a compact conversation list (search · New chat · New group) and, once a
 * row is tapped, that thread in place with a back arrow.
 *
 * Nothing here is chat logic of its own: the list is `useChatConversations` (15 s poll + focus, and
 * it keeps the FAB's unread badge in step), the thread is `ChatThread` (4 s forward poll while
 * visible, mark-read on open and on each new batch), the dialogs are the page's. The open room id
 * is owned by the launcher, so switching tabs or closing the panel and coming back keeps it.
 */
export function TeamChatPane({ openId, onOpenId }: { openId: string | null; onOpenId: (id: string | null) => void }) {
  const [search, setSearch] = useState('');
  const [newChat, setNewChat] = useState(false);
  const [newGroup, setNewGroup] = useState(false);

  // Keep polling the list while a thread is open too — it feeds the thread header before the
  // room's detail arrives, and the unread badge for the OTHER rooms.
  const listQ = useChatConversations();
  const rows = useMemo(() => listQ.data ?? [], [listQ.data]);
  const q = search.trim();
  const shown = filterConversations(rows, q);
  const staffOnly = isChatStaffOnlyError(listQ.error);

  const dialogs = (
    <>
      <NewChatDialog open={newChat} onClose={() => setNewChat(false)} onOpened={onOpenId} />
      <NewGroupDialog open={newGroup} onClose={() => setNewGroup(false)} onOpened={onOpenId} />
    </>
  );

  if (openId) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <ChatThread
          key={openId}
          id={openId}
          summary={rows.find((c) => c.id === openId)}
          onBack={() => onOpenId(null)}
          onExit={() => onOpenId(null)}
          compact
        />
        {dialogs}
      </div>
    );
  }

  const actionCls = 'border border-line text-content/70 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1.5 px-3 py-2">
        <Input
          size="sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search chats"
          prefix={<Search />}
          aria-label="Search chats"
          allowClear
          className="min-w-0 flex-1"
        />
        <Tooltip label="New chat">
          <IconButton aria-label="New chat" diameter={32} className={actionCls} onClick={() => setNewChat(true)}>
            <MessageSquarePlus />
          </IconButton>
        </Tooltip>
        <Tooltip label="New group">
          <IconButton aria-label="New group" diameter={32} className={actionCls} onClick={() => setNewGroup(true)}>
            <Users />
          </IconButton>
        </Tooltip>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto border-t border-line">
        {listQ.isLoading ? (
          <div className="p-3"><SkeletonText lines={6} /></div>
        ) : listQ.isError ? (
          <div className="p-3">
            <Alert
              type={staffOnly ? 'info' : 'error'}
              message={staffOnly ? 'Team chat is for Green Bay staff.' : chatErr(listQ.error, 'Could not load your chats')}
              action={staffOnly ? undefined : <Button size="sm" onClick={() => listQ.refetch()}>Retry</Button>}
            />
          </div>
        ) : shown.length === 0 ? (
          <EmptyState
            compact
            icon={<MessagesSquare />}
            title={q ? `No chats match “${q}”` : 'No conversations yet'}
            description={q ? undefined : 'Start a chat with a colleague or create a group.'}
            action={q ? undefined : (
              <div className="flex flex-wrap justify-center gap-2">
                <Button size="sm" variant="primary" icon={<MessageSquarePlus size={16} />} onClick={() => setNewChat(true)}>New chat</Button>
                <Button size="sm" icon={<Users size={16} />} onClick={() => setNewGroup(true)}>New group</Button>
              </div>
            )}
          />
        ) : (
          shown.map((c) => <ConversationRow key={c.id} c={c} compact onOpen={onOpenId} />)
        )}
      </div>

      {dialogs}
    </div>
  );
}
